import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Settings, Mail, Shield, Key, Plus, Trash2, Check, X, RotateCw, Star, Database, Loader2, AlertCircle, CheckCircle, RefreshCw, Unlock, Clock, Play, Bot } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';

// ─── Types ──────────────────────────────────────────

interface EmailConfig {
  id?: string;
  organizationId: string;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass?: string;
  smtpFrom: string;
  smtpSecure: boolean;
  smtpTls: boolean;
  isEnabled: boolean;
}

interface AiBotConfig {
  provider: 'ollama' | 'openrouter';
  baseUrl: string;
  model: string;
  apiKey: string | null;
  apiKeyConfigured: boolean;
  enabled: boolean;
}

interface AuthProvider {
  id: string;
  type: 'LDAP' | 'SAML' | 'OAUTH';
  name: string;
  isEnabled: boolean;
  isDefault: boolean;
  order: number;
  ldapUrl?: string;
  ldapBindDn?: string;
  ldapBaseDn?: string;
  ldapSearchFilter?: string;
  samlEntryPoint?: string;
  samlIssuer?: string;
  samlCert?: string;
  samlCallbackUrl?: string;
  samlLogoutUrl?: string;
  oauthProvider?: string;
  oauthClientId?: string;
  oauthClientSecret?: string;
  oauthTenantId?: string;
}

interface TestResult {
  ok: boolean;
  error?: string;
  latencyMs?: number;
}

type ProviderField = 'ldap' | 'saml' | 'oauth';

// ─── Provider Form Fields ───────────────────────────

const PROVIDER_FIELDS: Record<ProviderField, { key: string; label: string; placeholder: string }[]> = {
  ldap: [
    { key: 'ldapUrl', label: 'LDAP-URL', placeholder: 'ldaps://dc01.organisation.at:636' },
    { key: 'ldapBindDn', label: 'Bind DN', placeholder: 'CN=ServiceAccount,OU=Service,DC=org,DC=at' },
    { key: 'ldapBaseDn', label: 'Base DN', placeholder: 'OU=Benutzer,DC=org,DC=at' },
    { key: 'ldapSearchFilter', label: 'Suchfilter', placeholder: '(sAMAccountName={{username}})' },
  ],
  saml: [
    { key: 'samlEntryPoint', label: 'Entry Point URL', placeholder: 'https://idp.organisation.at/saml2/idp/SSOService.php' },
    { key: 'samlIssuer', label: 'Issuer', placeholder: 'https://datensammlung.rotkreuz.at' },
    { key: 'samlCert', label: 'X.509 Zertifikat', placeholder: '-----BEGIN CERTIFICATE-----' },
    { key: 'samlCallbackUrl', label: 'Callback URL', placeholder: 'https://datensammlung.rotkreuz.at/api/v1/auth/saml/callback' },
    { key: 'samlLogoutUrl', label: 'Logout URL (optional)', placeholder: '' },
  ],
  oauth: [
    { key: 'oauthProvider', label: 'Provider', placeholder: 'microsoft' },
    { key: 'oauthClientId', label: 'Client-ID', placeholder: '00000000-0000-0000-0000-000000000000' },
    { key: 'oauthClientSecret', label: 'Client-Secret', placeholder: '••••••••' },
    { key: 'oauthTenantId', label: 'Tenant-ID (optional)', placeholder: '' },
  ],
};

// ─── Component ───────────────────────────────────────



// ─── Security Sub-Components ────────────────────────

function SecurityNotifications({ orgId }: { orgId: string }) {
  const [enabled, setEnabled] = useState(false);
  const [chatId, setChatId] = useState('');
  const [emailAddr, setEmailAddr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/security/notifications?organizationId=' + orgId).then(res => {
      if (res.data) {
        setEnabled(res.data.alertOnFailedLogin || false);
        setChatId(res.data.telegramChatId || ''); setEmailAddr(res.data.alertEmail || '');
      }
    }).catch(() => {});
  }, [orgId]);

  async function save() {
    setSaving(true);
    try {
      await api.post('/admin/security/notifications', {
        organizationId: orgId,
        alertOnFailedLogin: enabled,
        telegramChatId: chatId || undefined, alertEmail: emailAddr || undefined,
      });
      alert('Gespeichert');
    } catch (e: any) {
      alert('Fehler: ' + (e.response?.data?.message || e.message));
    }
    setSaving(false);
  }

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" className="h-4 w-4" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
        <span className="text-sm">Login-Fehlversuche melden</span>
      </label>
      {enabled && (
        <div>
          <label className="text-xs text-gray-500">Telegram Chat ID</label>
          <input className="input-field text-sm" value={chatId} onChange={e => setChatId(e.target.value)} placeholder="z.B. 451271303" />
        </div>
      )}
      <button onClick={save} disabled={saving} className="btn-primary text-xs">
        {saving ? 'Speichern...' : 'Speichern'}
      </button>
    </div>
  );
}

function RateLimitConfig({ orgId }: { orgId: string }) {
  const [limit, setLimit] = useState(100);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/security/rate-limit?organizationId=' + orgId).then(res => {
      if (res.data?.limit != null) setLimit(res.data.limit);
    }).catch(() => {});
  }, [orgId]);

  async function save() {
    setSaving(true);
    try {
      await api.post('/admin/security/rate-limit', { organizationId: orgId, limit });
      alert('Gespeichert');
    } catch (e: any) {
      alert('Fehler: ' + (e.response?.data?.message || e.message));
    }
    setSaving(false);
  }

  return (
    <div className="flex items-center gap-3">
      <input type="number" className="input-field w-24 text-sm" value={limit} onChange={e => setLimit(Number(e.target.value))} min={0} />
      <span className="text-sm text-gray-500">Anfragen / Minute (0 = unbegrenzt)</span>
      <button onClick={save} disabled={saving} className="btn-primary text-xs">{saving ? '...' : 'Speichern'}</button>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const orgId = user?.organizationId || '';
  const isGsAdmin = user?.role === 'ADMIN_GENERALSEKRETARIAT';

  // KI-Bot state (nur Generalsekretariat)
  const [aiBotConfig, setAiBotConfig] = useState<AiBotConfig | null>(null);
  const [aiBotApiKey, setAiBotApiKey] = useState('');
  const [aiBotClearKey, setAiBotClearKey] = useState(false);
  const [aiBotDirty, setAiBotDirty] = useState(false);
  const [aiBotSaving, setAiBotSaving] = useState(false);
  const [aiBotToggling, setAiBotToggling] = useState(false);
  const [aiBotMessage, setAiBotMessage] = useState<{ ok: boolean; text: string } | null>(null);

  // Email state
  const [emailConfig, setEmailConfig] = useState<EmailConfig | null>(null);
  const [emailDirty, setEmailDirty] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState<TestResult | null>(null);

  // Auth provider state
  const [providers, setProviders] = useState<AuthProvider[]>([]);
  const [editingProvider, setEditingProvider] = useState<AuthProvider | null>(null);
  const [showNewProvider, setShowNewProvider] = useState(false);
  const [newProviderType, setNewProviderType] = useState<'LDAP' | 'SAML' | 'OAUTH'>('LDAP');
  const [newProviderForm, setNewProviderForm] = useState<Record<string, string>>({ name: '' });
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});

  // Backup state
  interface BackupConfigItem { id: string; organizationId: string; targetEndpoint: string; schedule: string; encryptionKey?: string | null; isActive: boolean; lastRunAt?: string | null; lastStatus?: string | null; lastError?: string | null; lastFileSize?: number | null; organization?: { id: string; name: string; level: string } | null; }
  const [backupConfigs, setBackupConfigs] = useState<BackupConfigItem[]>([]);
  const [showBackupForm, setShowBackupForm] = useState(false);
  const [backupForm, setBackupForm] = useState({ targetEndpoint: '', schedule: '0 2 * * *', encryptionKey: '', isActive: true });
  const [backupTestResults, setBackupTestResults] = useState<Record<string, TestResult>>({});
  const [backupRunResults, setBackupRunResults] = useState<Record<string, { ok: boolean; error?: string; fileSize?: number }>>({});
  const [backupRunning, setBackupRunning] = useState<Set<string>>(new Set());

  // Load
  useEffect(() => {
    if (!orgId) return;
    loadEmailConfig();
    loadProviders();
    loadBackups();
  }, [orgId]);

  useEffect(() => {
    if (isGsAdmin) loadAiBotConfig();
  }, [isGsAdmin]);

  async function loadAiBotConfig() {
    try {
      const { data } = await api.get('/ai-bot/config');
      setAiBotConfig(data);
      setAiBotApiKey('');
      setAiBotClearKey(false);
      setAiBotDirty(false);
    } catch { /* keine Berechtigung oder kein Zugriff */ }
  }

  async function loadEmailConfig() {
    try {
      const { data } = await api.get(`/admin/email-config?organizationId=${orgId}`);
      if (data) setEmailConfig({ ...data, smtpPass: '' });
    } catch { /* no config */ }
  }

  async function loadProviders() {
    try {
      const { data } = await api.get(`/admin/auth-providers?organizationId=${orgId}`);
      setProviders(data || []);
    } catch { /* no providers */ }
  }

  // ─── Email Actions ─────────────────────────────────

  async function saveEmailConfig() {
    if (!emailConfig) return;
    setEmailSaving(true);
    try {
      const payload: Record<string, unknown> = { ...emailConfig };
      if (!emailConfig.smtpPass) delete payload.smtpPass;
      await api.put(`/admin/email-config?organizationId=${orgId}`, payload);
      setEmailDirty(false);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert(`Fehler: ${e.response?.data?.message || e.message}`);
    } finally {
      setEmailSaving(false);
    }
  }

  async function testEmail() {
    setEmailTesting(true);
    setEmailTestResult(null);
    try {
      const { data } = await api.post(`/admin/email-config/test?organizationId=${orgId}`);
      setEmailTestResult(data);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setEmailTestResult({ ok: false, error: e.response?.data?.message || e.message });
    } finally {
      setEmailTesting(false);
    }
  }

  // ─── KI-Bot Actions ────────────────────────────────

  function setAiBot<K extends keyof AiBotConfig>(key: K, value: AiBotConfig[K]) {
    setAiBotConfig((prev) => (prev ? { ...prev, [key]: value } : null));
    setAiBotDirty(true);
    setAiBotMessage(null);
  }

  async function saveAiBotConfig() {
    if (!aiBotConfig) return;
    setAiBotSaving(true);
    setAiBotMessage(null);
    try {
      const payload: Record<string, unknown> = {
        provider: aiBotConfig.provider,
        baseUrl: aiBotConfig.baseUrl,
        model: aiBotConfig.model,
      };
      if (aiBotClearKey) payload.apiKey = '';
      else if (aiBotApiKey.trim()) payload.apiKey = aiBotApiKey.trim();

      const { data } = await api.patch('/ai-bot/config', payload);
      setAiBotConfig(data);
      setAiBotApiKey('');
      setAiBotClearKey(false);
      setAiBotDirty(false);
      setAiBotMessage({ ok: true, text: 'KI-Bot-Konfiguration gespeichert.' });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setAiBotMessage({ ok: false, text: e.response?.data?.message || e.message || 'Unbekannter Fehler' });
    } finally {
      setAiBotSaving(false);
    }
  }

  async function toggleAiBot(enabled: boolean) {
    setAiBotToggling(true);
    setAiBotMessage(null);
    try {
      const { data } = await api.post('/ai-bot/toggle', { enabled });
      // Nur den Aktivierungsstatus übernehmen, damit ungespeicherte
      // Änderungen an Provider, Modell oder Basis-URL nicht verloren gehen.
      setAiBotConfig((prev) => (prev ? { ...prev, enabled: data?.enabled ?? enabled } : data));
      setAiBotMessage({
        ok: true,
        text: enabled ? 'KI-Bot aktiviert.' : 'KI-Bot deaktiviert.',
      });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setAiBotMessage({ ok: false, text: e.response?.data?.message || e.message || 'Unbekannter Fehler' });
    } finally {
      setAiBotToggling(false);
    }
  }

  // ─── Auth Provider Actions ─────────────────────────

  function openNewProviderForm() {
    setNewProviderType('LDAP');
    setNewProviderForm({ name: '' });
    setShowNewProvider(true);
  }

  async function handleCreateProvider(e: React.FormEvent) {
    e.preventDefault();
    try {
      const payload: Record<string, unknown> = {
        type: newProviderType,
        name: newProviderForm.name,
        isEnabled: newProviderForm.isEnabled === 'true',
      };
      for (const field of PROVIDER_FIELDS[newProviderType.toLowerCase() as ProviderField]) {
        if (newProviderForm[field.key]) payload[field.key] = newProviderForm[field.key];
      }
      await api.post(`/admin/auth-providers?organizationId=${orgId}`, payload);
      setShowNewProvider(false);
      loadProviders();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert(`Fehler: ${e.response?.data?.message || e.message}`);
    }
  }

  function openEdit(p: AuthProvider) {
    setEditingProvider(p);
    const fields: Record<string, string> = { name: p.name, isEnabled: String(p.isEnabled) };
    const fieldKeys = PROVIDER_FIELDS[p.type.toLowerCase() as ProviderField] || [];
    for (const f of fieldKeys) {
      fields[f.key] = String((p as unknown as Record<string, unknown>)[f.key] ?? '');
    }
    if (p.type === 'OAUTH') fields.oauthClientSecret = '';
    setEditForm(fields);
  }

  async function handleUpdateProvider(e: React.FormEvent) {
    e.preventDefault();
    if (!editingProvider) return;
    try {
      const payload: Record<string, unknown> = { name: editForm.name };
      const fieldKeys = PROVIDER_FIELDS[editingProvider.type.toLowerCase() as ProviderField] || [];
      for (const f of fieldKeys) {
        if (editForm[f.key] !== undefined) payload[f.key] = editForm[f.key];
      }
      // oauthClientSecret nur senden wenn ausgefüllt
      if (editForm.oauthClientSecret === '') delete payload.oauthClientSecret;
      if (editForm.isEnabled !== undefined) payload.isEnabled = editForm.isEnabled === 'true';

      await api.put(`/admin/auth-providers/${editingProvider.id}`, payload);
      setEditingProvider(null);
      loadProviders();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert(`Fehler: ${e.response?.data?.message || e.message}`);
    }
  }

  async function deleteProvider(id: string) {
    if (!confirm('Provider wirklich löschen?')) return;
    try {
      await api.delete(`/admin/auth-providers/${id}`);
      loadProviders();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert(`Fehler: ${e.response?.data?.message || e.message}`);
    }
  }

  async function testProvider(id: string) {
    try {
      const { data } = await api.post(`/admin/auth-providers/${id}/test`);
      setTestResults((prev) => ({ ...prev, [id]: data }));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setTestResults((prev) => ({ ...prev, [id]: { ok: false, error: e.response?.data?.message || e.message } }));
    }
  }

  // ─── Backup Actions ────────────────────────────────

  async function loadBackups() {
    try {
      const { data } = await api.get(`/backup?organizationId=${orgId}`);
      setBackupConfigs(data || []);
    } catch { /* no configs */ }
  }

  async function handleSaveBackup(e: React.FormEvent) {
    e.preventDefault();
    try {
      await api.post(`/backup?organizationId=${orgId}`, backupForm);
      setShowBackupForm(false);
      setBackupForm({ targetEndpoint: '', schedule: '0 2 * * *', encryptionKey: '', isActive: true });
      loadBackups();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert(`Fehler: ${e.response?.data?.message || e.message}`);
    }
  }

  async function deleteBackup(id: string) {
    if (!confirm('Backup-Konfiguration wirklich löschen?')) return;
    try {
      await api.delete(`/backup/${id}`);
      loadBackups();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert(`Fehler: ${e.response?.data?.message || e.message}`);
    }
  }

  async function testBackupConn(id: string) {
    try {
      const { data } = await api.post(`/backup/${id}/test`);
      setBackupTestResults((prev) => ({ ...prev, [id]: data }));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setBackupTestResults((prev) => ({ ...prev, [id]: { ok: false, error: e.response?.data?.message || e.message } }));
    }
  }

  async function runBackup(id: string) {
    setBackupRunning((prev) => new Set(prev).add(id));
    try {
      const { data } = await api.post(`/backup/${id}/run`);
      setBackupRunResults((prev) => ({ ...prev, [id]: data }));
      loadBackups();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setBackupRunResults((prev) => ({ ...prev, [id]: { ok: false, error: e.response?.data?.message || e.message } }));
    } finally {
      setBackupRunning((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  }

  function formatDate(d: string | null | undefined) {
    if (!d) return null;
    try { return new Date(d).toLocaleString('de-DE'); } catch { return d; }
  }

  function formatBytes(bytes: number | null | undefined) {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024*1024) return (bytes/1024).toFixed(1) + ' KB';
    return (bytes/1024/1024).toFixed(2) + ' MB';
  }

  async function setDefault(id: string) {
    try {
      await api.post(`/admin/auth-providers/${id}/default?organizationId=${orgId}`);
      loadProviders();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert(`Fehler: ${e.response?.data?.message || e.message}`);
    }
  }

  function setEmail<K extends keyof EmailConfig>(key: K, value: EmailConfig[K]) {
    setEmailConfig((prev) => (prev ? { ...prev, [key]: value } : null));
    setEmailDirty(true);
  }

  // ─── Render ────────────────────────────────────────

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings size={28} className="text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Einstellungen</h1>
          <p className="text-sm text-gray-500">System- und Schnittstellenkonfiguration</p>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════ */}
      {/* EMAIL / SMTP                                  */}
      {/* ═══════════════════════════════════════════════ */}
      <section className="card">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
            <Mail size={20} />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-gray-900">Email / SMTP-Server</h2>
            <p className="text-sm text-gray-500">
              SMTP-Server für Benachrichtigungen (Deadline-Erinnerungen, Tageszusammenfassung)
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-xs text-gray-500">Aktiv</span>
            <input type="checkbox" className="sr-only peer" checked={emailConfig?.isEnabled ?? false} onChange={(e) => setEmail('isEnabled', e.target.checked)} />
            <div className="w-9 h-5 bg-gray-300 rounded-full peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-primary/30 transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4" />
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="SMTP-Host" value={emailConfig?.smtpHost ?? ''} placeholder="smtp.rotkreuz.at" onChange={(v) => setEmail('smtpHost', v)} />
          <Input label="Port" type="number" value={String(emailConfig?.smtpPort ?? 587)} placeholder="587" onChange={(v) => setEmail('smtpPort', parseInt(v) || 587)} />
          <Input label="Benutzername" value={emailConfig?.smtpUser ?? ''} placeholder="datensammlung@rotkreuz.at" onChange={(v) => setEmail('smtpUser', v)} />
          <Input label="Passwort (verschlüsselt gespeichert)" type="password" value={emailConfig?.smtpPass ?? ''} placeholder="••••••••" onChange={(v) => setEmail('smtpPass', v)} />
          <Input label="Absender (From)" value={emailConfig?.smtpFrom ?? ''} placeholder="Datenplattform <noreply@rotkreuz.at>" onChange={(v) => setEmail('smtpFrom', v)} />
          <div className="flex items-end gap-4">
            <Checkbox label="SSL (Port 465)" checked={emailConfig?.smtpSecure ?? false} onChange={(v) => setEmail('smtpSecure', v)} />
            <Checkbox label="TLS aktivieren" checked={emailConfig?.smtpTls ?? true} onChange={(v) => setEmail('smtpTls', v)} />
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4 pt-4 border-t">
          {emailDirty && (
            <button onClick={saveEmailConfig} disabled={emailSaving} className="btn-primary">
              {emailSaving ? 'Speichere…' : '💾 Speichern'}
            </button>
          )}
          <button onClick={testEmail} disabled={emailTesting} className="btn-secondary flex items-center gap-1 text-sm">
            <RotateCw size={14} className={emailTesting ? 'animate-spin' : ''} />
            Verbindung testen
          </button>
          {emailTestResult && (
            <span className={`text-sm flex items-center gap-1 ${emailTestResult.ok ? 'text-green-600' : 'text-red-600'}`}>
              {emailTestResult.ok ? <Check size={14} /> : <X size={14} />}
              {emailTestResult.ok ? `Verbunden (${emailTestResult.latencyMs}ms)` : emailTestResult.error}
            </span>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* KI-BOT                                        */}
      {/* ═══════════════════════════════════════════════ */}
      {isGsAdmin && aiBotConfig && (
        <section className="card">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary">
              <Bot size={20} />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-gray-900">KI-Bot</h2>
              <p className="text-sm text-gray-500">
                Assistent für das Anlegen von Datenfeldern und API-Endpunkten (Ollama oder OpenRouter).
                Schreibaktionen werden erst nach Bestätigung durch den Nutzer ausgeführt.
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-xs text-gray-500">Aktiv</span>
              <input
                type="checkbox"
                className="sr-only peer"
                checked={aiBotConfig.enabled}
                disabled={aiBotToggling}
                onChange={(e) => toggleAiBot(e.target.checked)}
              />
              <div className="w-9 h-5 bg-gray-300 rounded-full peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-primary/30 transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4" />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Provider</label>
              <select
                className="input-field"
                value={aiBotConfig.provider}
                onChange={(e) => setAiBot('provider', e.target.value as AiBotConfig['provider'])}
              >
                <option value="ollama">Ollama (lokal/intern)</option>
                <option value="openrouter">OpenRouter</option>
              </select>
            </div>
            <Input
              label="Modell"
              value={aiBotConfig.model}
              placeholder="z.B. llama3.1"
              onChange={(v) => setAiBot('model', v)}
            />
            <Input
              label="Basis-URL (Ollama)"
              value={aiBotConfig.baseUrl}
              placeholder="http://localhost:11434"
              onChange={(v) => setAiBot('baseUrl', v)}
            />
            <Input
              label="API-Key (verschlüsselt gespeichert)"
              type="password"
              value={aiBotApiKey}
              placeholder={aiBotConfig.apiKeyConfigured ? '•••••••• (gespeichert)' : 'Bei OpenRouter erforderlich'}
              onChange={(v) => {
                setAiBotApiKey(v);
                setAiBotDirty(true);
                setAiBotMessage(null);
              }}
            />
          </div>

          <div className="mt-4">
            <Checkbox
              label="Gespeicherten API-Key löschen"
              checked={aiBotClearKey}
              onChange={(v) => {
                setAiBotClearKey(v);
                setAiBotDirty(true);
                setAiBotMessage(null);
              }}
            />
          </div>

          <div className="flex items-center gap-3 mt-4 pt-4 border-t">
            {aiBotDirty && (
              <button onClick={saveAiBotConfig} disabled={aiBotSaving} className="btn-primary">
                {aiBotSaving ? 'Speichere…' : '💾 Speichern'}
              </button>
            )}
            {aiBotMessage && (
              <span className={`text-sm flex items-center gap-1 ${aiBotMessage.ok ? 'text-green-600' : 'text-red-600'}`}>
                {aiBotMessage.ok ? <Check size={14} /> : <X size={14} />}
                {aiBotMessage.text}
              </span>
            )}
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* AUTH-PROVIDER                                */}
      {/* ═══════════════════════════════════════════════ */}
      <section className="card">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 text-red-600">
            <Shield size={20} />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-gray-900">Authentifizierungs-Provider</h2>
            <p className="text-sm text-gray-500">
              LDAP(S), SAML 2.0 und OAuth 2.0 / OpenID Connect. Secrets AES-256-GCM verschlüsselt.
            </p>
          </div>
          <button onClick={openNewProviderForm} className="btn-primary flex items-center gap-1 text-sm">
            <Plus size={14} /> Provider
          </button>
        </div>

        {providers.length === 0 && (
          <p className="text-sm text-gray-400 py-4">
            Noch keine Auth-Provider konfiguriert. Lokale JWT-Authentifizierung ist aktiv.
          </p>
        )}

        {providers.map((p) => (
          <div key={p.id} className="border rounded-lg p-4 mb-3 last:mb-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${p.type === 'LDAP' ? 'bg-blue-100 text-blue-700' : p.type === 'SAML' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                  {p.type}
                </span>
                <span className="font-medium text-gray-900">{p.name}</span>
                {p.isEnabled ? (
                  <span className="text-xs text-green-600 flex items-center gap-1"><Check size={12} /> Aktiv</span>
                ) : (
                  <span className="text-xs text-gray-400 flex items-center gap-1"><X size={12} /> Inaktiv</span>
                )}
                {p.isDefault && <Star size={14} className="text-yellow-500" aria-label="Standard-Provider" />}
              </div>
              <div className="flex items-center gap-1">
                {!p.isDefault && (
                  <button onClick={() => setDefault(p.id)} className="p-1 text-gray-400 hover:text-yellow-500 transition-colors" title="Als Standard setzen">
                    <Star size={14} />
                  </button>
                )}
                <button onClick={() => testProvider(p.id)} className="p-1 text-gray-400 hover:text-blue-500 transition-colors" title="Testen">
                  <RotateCw size={14} />
                </button>
                <button onClick={() => openEdit(p)} className="p-1 text-gray-400 hover:text-orange-500 transition-colors" title="Bearbeiten">
                  <Settings size={14} />
                </button>
                <button onClick={() => deleteProvider(p.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors" title="Löschen">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500 space-y-0.5">
              {p.type === 'LDAP' && p.ldapUrl && <div>URL: {p.ldapUrl}</div>}
              {p.type === 'LDAP' && p.ldapBaseDn && <div>Base DN: {p.ldapBaseDn}</div>}
              {p.type === 'SAML' && p.samlEntryPoint && <div>Entry: {p.samlEntryPoint}</div>}
              {p.type === 'OAUTH' && p.oauthProvider && <div>Provider: {p.oauthProvider}</div>}
              {p.type === 'OAUTH' && p.oauthClientId && <div>Client-ID: {p.oauthClientId}</div>}
            </div>
            {testResults[p.id] && (
              <div className={`mt-2 text-xs flex items-center gap-1 ${testResults[p.id].ok ? 'text-green-600' : 'text-red-600'}`}>
                {testResults[p.id].ok ? <Check size={12} /> : <X size={12} />}
                {testResults[p.id].ok ? 'Konfiguration valide' : testResults[p.id].error}
              </div>
            )}
          </div>
        ))}
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* BACKUP                                         */}
      {/* ═══════════════════════════════════════════════ */}
      <section className="card">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-600">
            <Database size={20} />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-gray-900">Backup & Replikation</h2>
            <p className="text-sm text-gray-500">
              pg_dump-Backups, S3/SFTP/rsync-Ziele. Secrets AES-256-GCM verschlüsselt.
            </p>
          </div>
        </div>

        {backupConfigs.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">Keine Backup-Konfigurationen vorhanden.</p>
        ) : (
          <div className="space-y-3">
            {backupConfigs.map((bc) => (
              <div key={bc.id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 text-sm">{bc.targetEndpoint}</span>
                    <span className="text-xs text-gray-400">({bc.schedule})</span>
                    {bc.isActive ? <span className="text-xs text-green-600">● Aktiv</span> : <span className="text-xs text-gray-400">○ Inaktiv</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => testBackupConn(bc.id)} className="p-1 text-gray-400 hover:text-blue-500 transition-colors" title="Testen">
                      <RotateCw size={14} />
                    </button>
                    <button onClick={() => deleteBackup(bc.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors" title="Löschen">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {backupTestResults[bc.id] && (
                  <div className={`mt-1 text-xs flex items-center gap-1 ${backupTestResults[bc.id].ok ? 'text-green-600' : 'text-red-600'}`}>
                    {backupTestResults[bc.id].ok ? <Check size={12} /> : <X size={12} />}
                    {backupTestResults[bc.id].ok ? `Verbindung OK (${backupTestResults[bc.id].latencyMs}ms)` : backupTestResults[bc.id].error}
                  </div>
                )}
                {bc.lastRunAt && (
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                    <Clock size={11} />
                    Letzter Lauf: {formatDate(bc.lastRunAt)}
                    {bc.lastStatus === 'ok' && <span className="text-green-600 flex items-center gap-0.5"><Check size={11} /> Erfolg</span>}
                    {bc.lastStatus === 'failed' && <span className="text-red-600" title={bc.lastError || ''}>Fehlgeschlagen</span>}
                    {bc.lastStatus === 'running' && <span className="text-blue-600 animate-pulse">Laeuft...</span>}
                    {bc.lastFileSize && <span className="text-gray-400">({formatBytes(bc.lastFileSize)})</span>}
                  </div>
                )}
                {backupRunResults[bc.id] && (
                  <div className={`mt-1 text-xs flex items-center gap-1 ${backupRunResults[bc.id].ok ? 'text-green-600' : 'text-red-600'}`}>
                    {backupRunResults[bc.id].ok ? <Check size={12} /> : <X size={12} />}
                    {backupRunResults[bc.id].ok
                      ? `Backup erfolgreich (${formatBytes(backupRunResults[bc.id].fileSize)})`
                      : backupRunResults[bc.id].error}
                  </div>
                )}
                <div className="mt-2">
                  <button
                    onClick={() => runBackup(bc.id)}
                    disabled={backupRunning.has(bc.id)}
                    className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1"
                  >
                    {backupRunning.has(bc.id) ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                    {backupRunning.has(bc.id) ? 'Sichere...' : 'Jetzt sichern'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showBackupForm && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <form onSubmit={handleSaveBackup} className="space-y-3">
              <Input label="Ziel (S3/SFTP/rsync)" value={backupForm.targetEndpoint} onChange={(v) => setBackupForm({ ...backupForm, targetEndpoint: v })} placeholder="s3://bucket/backups/" required />
              <Input label="Cron-Plan (optional)" value={backupForm.schedule} onChange={(v) => setBackupForm({ ...backupForm, schedule: v })} placeholder="0 2 * * *" />
              <Input label="Verschlüsselungs-Key (optional)" type="password" value={backupForm.encryptionKey} onChange={(v) => setBackupForm({ ...backupForm, encryptionKey: v })} placeholder="AES-256-GCM verschlüsselt" />
              <Checkbox label="Aktiv" checked={backupForm.isActive} onChange={(v) => setBackupForm({ ...backupForm, isActive: v })} />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowBackupForm(false)} className="btn-secondary text-sm">Abbrechen</button>
                <button type="submit" className="btn-primary text-sm">Speichern</button>
              </div>
            </form>
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-gray-100">
          <button onClick={() => setShowBackupForm(!showBackupForm)} className="btn-secondary text-sm flex items-center gap-1">
            <Plus size={14} />{showBackupForm ? 'Schließen' : 'Backup-Ziel hinzufügen'}
          </button>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* SICHERHEIT                                    */}
      {/* ═══════════════════════════════════════════════ */}
      <section className="card">
        <div className="flex items-start gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
            <Key size={20} />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Sicherheit</h2>
            <p className="text-sm text-gray-500">Login-Alerts & Rate-Limiting</p>
          </div>
        </div>
        
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Login-Benachrichtigungen</h3>
            <SecurityNotifications orgId={orgId} />
          </div>
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Rate Limiting</h3>
            <RateLimitConfig orgId={orgId} />
          </div>
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Brute-Force-Sperren</h3>
            <p className="text-xs text-gray-400 mb-2">Nach 5 Fehlversuchen (15 Min) für 30 Min blockiert.</p>
            <BruteForceBlockList />
          </div>
          <div className="border-t border-gray-100 pt-4 text-sm text-gray-600 space-y-1">
            <p>✅ <strong>AES-256-GCM</strong> — Secrets verschlüsselt in DB.</p>
            <p>✅ <strong>TLS 1.3</strong> — Erzwungen (Helmet + HSTS).</p>
            <p>✅ <strong>SHA-256</strong> — Revisionssichere Audit-Logs.</p>
            <p>✅ <strong>DSGVO</strong> — Personenbezogene Daten geschützt.</p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════ */}
      {/* MODAL: Neuer Provider                         */}
      {/* ═══════════════════════════════════════════════ */}
      {showNewProvider && (
        <Modal title="Neuer Auth-Provider" onClose={() => setShowNewProvider(false)}>
          <form onSubmit={handleCreateProvider} className="space-y-4">
            <div className="flex gap-2 mb-3">
              {(['LDAP', 'SAML', 'OAUTH'] as const).map((t) => (
                <button key={t} type="button" onClick={() => { setNewProviderType(t); }} className={`px-3 py-1 rounded text-sm font-medium transition-colors ${newProviderType === t ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {t}
                </button>
              ))}
            </div>
            <Input label="Name" value={newProviderForm.name || ''} onChange={(v) => setNewProviderForm((p) => ({ ...p, name: v }))} placeholder="z.B. 'Active Directory Wien'" required />
            {PROVIDER_FIELDS[newProviderType.toLowerCase() as ProviderField].map((f) => (
              <Input key={f.key} label={f.label} value={newProviderForm[f.key] || ''} onChange={(v) => setNewProviderForm((p) => ({ ...p, [f.key]: v }))} placeholder={f.placeholder} type={f.key.includes('Secret') ? 'password' : 'text'} />
            ))}
            <Checkbox label="Aktiviert" checked={newProviderForm.isEnabled === 'true'} onChange={(v) => setNewProviderForm((p) => ({ ...p, isEnabled: String(v) }))} />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowNewProvider(false)} className="btn-secondary text-sm">Abbrechen</button>
              <button type="submit" className="btn-primary text-sm">Erstellen</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* MODAL: Provider bearbeiten                    */}
      {/* ═══════════════════════════════════════════════ */}
      {editingProvider && (
        <Modal title={`Bearbeiten: ${editingProvider.name}`} onClose={() => setEditingProvider(null)}>
          <form onSubmit={handleUpdateProvider} className="space-y-4">
            <Input label="Name" value={editForm.name || ''} onChange={(v) => setEditForm((p) => ({ ...p, name: v }))} placeholder="Provider-Name" required />
            {PROVIDER_FIELDS[editingProvider.type.toLowerCase() as ProviderField].map((f) => (
              <Input key={f.key} label={f.label} value={editForm[f.key] || ''} onChange={(v) => setEditForm((p) => ({ ...p, [f.key]: v }))} placeholder={f.placeholder} type={f.key.includes('Secret') || f.key.includes('Cert') ? 'password' : 'text'} />
            ))}
            <Checkbox label="Aktiviert" checked={editForm.isEnabled === 'true'} onChange={(v) => setEditForm((p) => ({ ...p, isEnabled: String(v) }))} />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditingProvider(null)} className="btn-secondary text-sm">Abbrechen</button>
              <button type="submit" className="btn-primary text-sm">Speichern</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ─── Reusable Components ─────────────────────────────

function Input({ label, type = 'text', value, placeholder, required, onChange }: {
  label: string; type?: string; value: string; placeholder?: string; required?: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="text-sm text-gray-700 block mb-1">{label}</label>
      <input type={type} className="input-field" placeholder={placeholder} value={value} required={required} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
      <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function BruteForceBlockList() {
  const { data: blocked, isLoading, error, refetch } = useQuery({
    queryKey: ['bruteforce'],
    queryFn: async () => {
      const { data } = await api.get('/admin/bruteforce');
      return data;
    },
    refetchInterval: 10000,
  });

  const unblockMutation = useMutation({
    mutationFn: async ({ ip, identifier }: { ip: string; identifier: string }) => {
      await api.post('/admin/bruteforce/unblock', { ip, identifier });
    },
    onSuccess: () => refetch(),
  });

  return (
    <div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          Fehler beim Laden
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 size={16} className="animate-spin" /> Lade...
        </div>
      )}

      {blocked && blocked.length === 0 && (
        <div className="text-sm text-gray-400 bg-gray-50 rounded-lg p-4 text-center">
          <CheckCircle size={24} className="mx-auto mb-2 text-green-400" />
          Keine blockierten Zugriffe
        </div>
      )}

      {blocked && blocked.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-3 py-2 font-medium text-gray-600">IP-Adresse</th>
                  <th className="px-3 py-2 font-medium text-gray-600">User</th>
                  <th className="px-3 py-2 font-medium text-gray-600">Versuche</th>
                  <th className="px-3 py-2 font-medium text-gray-600">Sperrdauer</th>
                  <th className="px-3 py-2 font-medium text-gray-600">Status</th>
                  <th className="px-3 py-2 font-medium text-gray-600">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {blocked.map((entry: any) => (
                  <tr key={entry.key} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{entry.ip}</td>
                    <td className="px-3 py-2">{entry.identifier.replace('login:', '')}</td>
                    <td className="px-3 py-2">{entry.count}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {entry.blockedUntil ? (
                        entry.remaining > 0 ? (
                          <span className="text-red-500">{Math.floor(entry.remaining / 60)}:{String(entry.remaining % 60).padStart(2, '0')} min</span>
                        ) : (
                          <span className="text-green-500">Abgelaufen</span>
                        )
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {entry.blockedUntil && entry.remaining > 0 ? (
                        <span className="badge-danger text-[10px]">Geblockt</span>
                      ) : (
                        <span className="badge-warning text-[10px]">Aktiv</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => unblockMutation.mutate({ ip: entry.ip, identifier: entry.identifier })}
                        disabled={unblockMutation.isPending}
                        className="btn-secondary text-xs px-2 py-1 flex items-center gap-1"
                      >
                        <Unlock size={12} />
                        Entsperren
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 flex justify-between items-center">
            <span>{blocked.length} Eintrag{blocked.length !== 1 ? 'e' : ''}</span>
            <button onClick={() => refetch()} className="text-blue-500 hover:underline flex items-center gap-1">
              <RefreshCw size={12} /> Aktualisieren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
